import { API_ROUTES } from '@config/routes';
import api from './api';
import type { ClientPortalQuoteStatus, ClientPortalSession } from '@/types';

const withProjectParams = (projectId?: string) =>
  projectId
    ? {
        params: { projectId },
      }
    : undefined;

export const clientPortalService = {
  getProjectPortal: async (projectId: string): Promise<ClientPortalSession> =>
    api.post(API_ROUTES.CLIENT_PORTALS.PROJECT(projectId), {}),

  getPublicPortal: async (shareToken: string, projectId?: string): Promise<ClientPortalSession> =>
    api.get(API_ROUTES.CLIENT_PORTALS.BY_SHARE_TOKEN(shareToken), withProjectParams(projectId)),

  respondToQuote: async (
    shareToken: string,
    status: Extract<ClientPortalQuoteStatus, 'accepted' | 'declined'>,
    quoteDocumentId?: string,
    projectId?: string
  ): Promise<ClientPortalSession> =>
    api.post(
      API_ROUTES.CLIENT_PORTALS.QUOTE_RESPONSE(shareToken),
      { status, quoteDocumentId },
      withProjectParams(projectId)
    ),

  signContract: async (
    shareToken: string,
    legalName: string,
    contractFormId?: string,
    projectId?: string
  ): Promise<ClientPortalSession> =>
    api.post(
      API_ROUTES.CLIENT_PORTALS.SIGN_CONTRACT(shareToken),
      { legalName, contractFormId },
      withProjectParams(projectId)
    ),

  submitQuestionnaire: async (
    shareToken: string,
    responses: Record<string, string>,
    markComplete = false,
    questionnaireFormId?: string,
    projectId?: string
  ): Promise<ClientPortalSession> =>
    api.post(
      API_ROUTES.CLIENT_PORTALS.QUESTIONNAIRE(shareToken),
      { responses, markComplete, questionnaireFormId },
      withProjectParams(projectId)
    ),

  startPaymentPlan: async (
    shareToken: string,
    scheduleId: string,
    schedule: { id: string; name: string; payments: Array<Record<string, unknown>> },
    invoiceId?: string,
    projectId?: string
  ): Promise<ClientPortalSession> =>
    api.post(
      API_ROUTES.CLIENT_PORTALS.PAYMENT_PLAN(shareToken),
      { scheduleId, schedule, invoiceId },
      withProjectParams(projectId)
    ),

  submitPaymentRequest: async (
    shareToken: string,
    payment: {
      amount: number;
      method: 'card' | 'transfer';
      note?: string;
      paidAt?: string;
      invoiceId?: string;
    },
    projectId?: string
  ): Promise<ClientPortalSession> =>
    api.post(
      API_ROUTES.CLIENT_PORTALS.PAYMENT_REQUEST(shareToken),
      payment,
      withProjectParams(projectId)
    ),
};
